import { Dialog, DialogTitle } from '@mui/material'
import { useCallback, useEffect, useRef } from 'react'

import {
	ImportSqlDialogProps,
	SqlImportFormValues,
} from '@/features/sqlImport/types'
import { useAppContext } from '@/hooks'

import { useImportSql } from '../../hooks'
import { ImportDialogActions, ImportDialogContent } from './components'
import { useImportForm } from './hooks/useImportForm'

export const SqlImportModal = ({
	open,
	structureType,
	structureId,
	structureName,
	onClose,
	onSuccess,
}: ImportSqlDialogProps) => {
	const { t } = useAppContext()
	const { progress, importSql, resetProgress } = useImportSql(
		structureType,
		structureId,
		structureName,
	)
	const successCalledRef = useRef(false)

	const {
		control,
		handleSubmit,
		inputMode,
		selectedFile,
		sqlText,
		errors,
		setValue,
		trigger,
		reset: resetForm,
	} = useImportForm()

	const onSubmit = async (data: SqlImportFormValues) => {
		if (data.inputMode === 'file' && data.selectedFile) {
			await importSql(data.selectedFile)
		} else if (data.inputMode === 'paste') {
			await importSql(data.sqlText)
		}
	}

	const handleFileSelect = async (files: File[]) => {
		setValue('selectedFile', files[0] || null)
		await trigger('selectedFile')
	}

	const handleSqlTextChange = async (value: string) => {
		if (typeof value === 'string') {
			setValue('sqlText', value)
			await trigger('sqlText')
		}
	}

	const handleFormSubmit = handleSubmit(onSubmit)

	const handleClose = useCallback(() => {
		if (progress.status === 'importing') {
			return
		}

		resetProgress()
		resetForm()
		successCalledRef.current = false
		onClose()
	}, [onClose, progress.status, resetProgress, resetForm])

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && progress.status !== 'importing') {
				handleClose()
			}
		}

		if (open) {
			document.addEventListener('keydown', handleKeyDown)
			return () => document.removeEventListener('keydown', handleKeyDown)
		}
	}, [open, progress.status, handleClose])

	useEffect(() => {
		if (
			progress.status === 'success' &&
			onSuccess &&
			!successCalledRef.current
		) {
			successCalledRef.current = true
			onSuccess()
		}
	}, [progress.status, onSuccess])

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			disableEscapeKeyDown={progress.status === 'importing'}
		>
			<DialogTitle>{t('IMPORT_SQL_DIALOG_TITLE')}</DialogTitle>

			<ImportDialogContent
				progress={progress}
				control={control}
				inputMode={inputMode}
				selectedFile={selectedFile}
				sqlText={sqlText}
				errors={errors}
				onFileSelect={handleFileSelect}
				onSqlTextChange={handleSqlTextChange}
			/>

			<ImportDialogActions
				progress={progress}
				inputMode={inputMode}
				selectedFile={selectedFile}
				sqlText={sqlText}
				errors={errors}
				onSubmit={handleFormSubmit}
				onClose={handleClose}
				onResetProgress={resetProgress}
			/>
		</Dialog>
	)
}
